import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

interface AdminNavItem {
  labelKey: string;
  /** Typed icon descriptor passed directly to svg[sdUseIcon]. */
  icon: fromSeriousSystem.IconsType;
  /** Route to navigate to. null = item is disabled (out-of-scope). */
  routerLink: string | null;
}

@Component({
  selector: 'squadbox-admin-navigation',
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseIconDirective,
  ],
  template: `
    <div
      class="
        h-full w-70 shrink-0
        bg-shades-white border-r border-neutral-200
        shadow
        flex flex-col overflow-hidden
        [view-transition-name:admin-navigation]
      "
    >
      <!-- Close-admin button: returns to the main app, not browser history -->
      <div
        class="h-16 px-4 flex items-end pb-0 pt-4 shrink-0 sticky top-0 bg-shades-white"
      >
        <button
          type="button"
          (click)="handleCloseClick()"
          class="
            size-12 rounded-full flex items-center justify-center
            hover:bg-neutral-100 transition-colors cursor-pointer
          "
          aria-label="Close admin"
        >
          <svg
            class="size-6 text-neutral-700"
            sdUseArrowIcon="arrow-left"
          ></svg>
        </button>
      </div>

      <!-- Nav list -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden pt-4 pb-6 px-1">
        <div class="flex flex-col gap-1 w-full">
          <!-- Section label -->
          <div class="h-8 px-4 flex items-center">
            <span class="typo-caption font-semibold text-neutral-500 truncate">
              {{ 'ADMIN.TITLE' | translate }}
            </span>
          </div>

          <!-- Nav items -->
          @for (item of navItems(); track item.labelKey) {
            @if (item.routerLink) {
              <!-- Enabled item: linked and active-aware -->
              <a
                [routerLink]="item.routerLink"
                routerLinkActive
                #rla="routerLinkActive"
                class="
                  flex items-center gap-3 px-3 py-2 rounded w-full
                  hover:bg-primary-50/60 transition-colors cursor-pointer
                "
                [class.bg-primary-50]="rla.isActive"
              >
                <div
                  class="size-12 rounded-lg shrink-0 flex items-center justify-center transition-colors overflow-clip"
                  [class.bg-primary-100]="rla.isActive"
                  [class.bg-primary-50]="!rla.isActive"
                >
                  <svg
                    class="size-6 text-primary-300"
                    [sdUseIcon]="item.icon"
                  ></svg>
                </div>
                <span
                  class="typo-p3 font-semibold text-neutral-700 whitespace-nowrap"
                >
                  {{ item.labelKey | translate }}
                </span>
              </a>
            } @else {
              <!-- Disabled item: out-of-scope stub -->
              <div
                class="
                  flex items-center gap-3 px-3 py-2 rounded w-full
                  opacity-50 cursor-not-allowed select-none
                "
              >
                <div
                  class="size-12 rounded-lg shrink-0 flex items-center justify-center bg-primary-50 overflow-clip"
                >
                  <svg
                    class="size-6 text-primary-300"
                    [sdUseIcon]="item.icon"
                  ></svg>
                </div>
                <span
                  class="typo-p3 font-semibold text-neutral-700 whitespace-nowrap"
                >
                  {{ item.labelKey | translate }}
                </span>
              </div>
            }
          }
        </div>
      </div>
    </div>
  `,
})
export class AdminNavigationComponent {
  private readonly router = inject(Router);

  public readonly navItems = computed<AdminNavItem[]>(() => [
    {
      labelKey: 'ADMIN.NAV.USERS',
      icon: {
        type: 'navigation',
        icon: 'settings-utilisateurs',
      } satisfies fromSeriousSystem.NavigationIconsInterface,
      routerLink: '/admin/users',
    },
    {
      labelKey: 'ADMIN.NAV.GROUPS',
      icon: {
        type: 'navigation',
        icon: 'settings-groupe',
      } satisfies fromSeriousSystem.NavigationIconsInterface,
      routerLink: '/admin/groups',
    },
    {
      labelKey: 'ADMIN.NAV.TEAMMATES',
      icon: {
        type: 'navigation',
        icon: 'settings-teammate',
      } satisfies fromSeriousSystem.NavigationIconsInterface,
      routerLink: '/admin/teammates',
    },
  ]);

  /**
   * The sidebar arrow closes the admin area and returns to the main app
   * (chats) rather than walking browser history: admin is a full-page detour,
   * so "back" means "exit admin", not "previous router state" (which could
   * land on an arbitrary prior URL or leave the SPA entirely).
   */
  public handleCloseClick(): void {
    void this.router.navigate(['/chats']);
  }
}
